import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useRouter } from 'next/compat/router'
import { useSelector } from 'react-redux'

import { Box, HStack, Spacer, Stack, useDisclosure } from '@chakra-ui/react'
import { LayoutGroup, motion } from 'framer-motion'

import { Button, FiltersButton, SearchInput } from '@/components/ui'
import {
  IncidentsView,
  selectActiveFilter,
  selectIncidentFilterCount,
} from '@/redux/incidents/incidentsSlice'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'
import { DASHBOARD } from '@/utils/routes'

import {
  ActiveFilterBadge,
  IncidentsFilterDrawer,
} from '../incidents-filter-drawer'
import { IncidentsViewToggle } from './IncidentsViewToggle'
import { NextUpButton } from './NextUpButton'

interface IProps {
  filter?: string
  setFilter?: React.Dispatch<React.SetStateAction<string>>
  isPriorityQueueActive?: boolean
  incidentsView?: IncidentsView
  onSelectView?: (view: IncidentsView) => void
}

export const IncidentsSearchFilterHeader = ({
  filter,
  setFilter,
  isPriorityQueueActive = false,
  incidentsView = 'all',
  onSelectView = () => {},
}: IProps) => {
  const { shouldShowMetricsBtn } = usePermissions()
  const { push } = useRouter()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const filterCount = useSelector(selectIncidentFilterCount)
  const activeFilter = useSelector(selectActiveFilter)
  const isPriorityQueueEnabled = useFeatureFlag(FeatureFlag.priorityQueue)

  return (
    <>
      <Box
        bgColor='white'
        borderBottom='1px solid #D5DCE4'
        boxShadow='lg'
        px='6'
        py='4'
        w='full'
      >
        <Stack
          alignItems='start'
          direction={{ base: 'column', md: 'row' }}
          justifyContent='start'
          m='auto'
        >
          <HStack spacing='2'>
            <Box
              alignItems='center'
              display='flex'
              flexDir='row'
              flexShrink={0}
              w='300px'
            >
              <SearchInput
                dataTestId='incidentsPage_tableHeader_search'
                onChange={(e) => setFilter?.(e.target.value)}
                placeholder='Search or Filter'
                value={filter ?? ''}
              />
            </Box>
            <LayoutGroup>
              <motion.div
                layout
                style={{ display: 'flex' }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
              >
                <IncidentsViewToggle
                  onSelectView={onSelectView}
                  view={incidentsView}
                />
              </motion.div>
              {isPriorityQueueEnabled && (
                <motion.div
                  layout
                  style={{ display: 'flex' }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  <NextUpButton isActive={isPriorityQueueActive} />
                </motion.div>
              )}
              {shouldShowMetricsBtn && (
                <motion.div
                  layout
                  style={{ display: 'flex' }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  <Button
                    fontSize='12px'
                    fontWeight='500'
                    h='40px'
                    onClick={() => {
                      trackEvent(
                        `${BUTTON_PRESS} Metrics - from Incidents page`
                      )
                      push(DASHBOARD)
                    }}
                    rounded='6px'
                    variant='secondary'
                  >
                    Metrics
                  </Button>
                </motion.div>
              )}
            </LayoutGroup>
          </HStack>
          <Spacer />
          <motion.div
            layout
            style={{ display: 'flex' }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <HStack>
              {activeFilter && <ActiveFilterBadge label={activeFilter?.name} />}
              <FiltersButton
                data-testid='incidentsPage_tableHeader_filterButton'
                filterCount={filterCount}
                onOpen={onOpen}
              />
            </HStack>
          </motion.div>
        </Stack>
      </Box>
      <IncidentsFilterDrawer isOpen={isOpen} onClose={onClose} />
    </>
  )
}
